import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Grid,
  Divider,
  Link,
  IconButton,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Security as SecurityIcon,
  OpenInNew as ExternalLinkIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAccount, useBalance, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { parseEther, formatEther } from 'viem';
import { Project } from '../services/projectService';

// Investment contract ABI (simplified for demo)
const INVESTMENT_ABI = [
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "tokenContract",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "amount",
        "type": "uint256"
      }
    ],
    "name": "invest",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  }
];

interface InvestmentModalProps {
  open: boolean;
  onClose: () => void;
  project: Project | null;
}

const steps = ['Connect Wallet', 'Enter Amount', 'Review & Pay', 'Confirmation'];

const InvestmentModal: React.FC<InvestmentModalProps> = ({ open, onClose, project }) => {
  const { address, isConnected } = useAccount();
  const [activeStep, setActiveStep] = useState(0);
  const [investmentAmount, setInvestmentAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [transactionHash, setTransactionHash] = useState<string | ''>('');

  // Get user's ETH balance
  const { data: balance } = useBalance({
    address: address,
  });

  // Write contract for investment
  const { writeContract, data: hash, isPending } = useWriteContract();

  // Wait for transaction
  const { isLoading: isTransactionPending, isSuccess: isTransactionSuccess } = useWaitForTransactionReceipt({
    hash,
  });

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setInvestmentAmount('');
      setError(null);
      setSuccess(false);
      setTransactionHash('');
    }
  }, [open]);

  useEffect(() => {
    if (isTransactionSuccess && hash) {
      setTransactionHash(hash);
      setSuccess(true);
      setActiveStep(3);
      setLoading(false);
    }
  }, [isTransactionSuccess, hash]);

  const handleNext = () => {
    if (activeStep === 0 && !isConnected) {
      setError('Please connect your wallet first');
      return;
    }
    if (activeStep === 1 && !investmentAmount) {
      setError('Please enter an investment amount');
      return;
    }
    if (activeStep === 1 && parseFloat(investmentAmount) <= 0) {
      setError('Investment amount must be greater than 0');
      return;
    }
    if (activeStep === 1 && balance && parseFloat(investmentAmount) > parseFloat(formatEther(balance.value))) {
      setError('Insufficient ETH balance');
      return;
    }
    
    setError(null);
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleInvest = async () => {
    if (!project || !investmentAmount || !project.smart_contract_address) return;

    setLoading(true);
    setError(null);

    try {
      // Execute the investment transaction
      writeContract({
        address: project.smart_contract_address as `0x${string}`,
        abi: INVESTMENT_ABI,
        functionName: 'invest',
        args: [
          project.smart_contract_address as `0x${string}`,
          parseEther(investmentAmount)
        ],
        value: parseEther(investmentAmount),
      });
    } catch (error) {
      console.error('Investment failed:', error);
      setError('Investment failed. Please try again.');
      setLoading(false);
    }
  };

  const calculateTokens = () => {
    if (!project || !investmentAmount) return 0;
    const investmentValue = parseFloat(investmentAmount);
    const projectValue = project.valuation_usd || 0;
    const tokenizedPercent = project.tokenized_percent || 0;
    const totalTokens = (projectValue * tokenizedPercent) / 100;
    return (investmentValue / projectValue) * totalTokens;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getExplorerUrl = (hash: string) => {
    return `https://explorer.hoodi.ethpandaops.io/tx/${hash}`;
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <WalletIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Connect Your Wallet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Connect your wallet to invest in this tokenized asset on Hoodi testnet
              </Typography>
              
              {isConnected ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Wallet connected: {address?.slice(0, 6)}...{address?.slice(-4)}
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Please connect your wallet to continue
                </Alert>
              )}
              
              {balance && (
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Available Balance
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {formatEther(balance.value)} ETH
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          </motion.div>
        );

      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ py: 2 }}>
              <Typography variant="h6" gutterBottom>
                Investment Amount
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enter the amount of ETH you want to invest
              </Typography>
              
              <TextField
                fullWidth
                type="number"
                label="Investment Amount (ETH)"
                value={investmentAmount}
                onChange={(e) => setInvestmentAmount(e.target.value)}
                InputProps={{
                  endAdornment: <Typography variant="body2">ETH</Typography>,
                }}
                sx={{ mb: 3 }}
              />
              
              {investmentAmount && (
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Investment Value
                        </Typography>
                        <Typography variant="h6" color="primary.main">
                          ${(parseFloat(investmentAmount) * 2000).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Tokens to Receive
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {calculateTokens().toLocaleString()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Box>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ py: 2 }}>
              <Typography variant="h6" gutterBottom>
                Review Investment
              </Typography>
              
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {project?.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {project?.description}
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Investment Amount
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        {investmentAmount} ETH
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        USD Value
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        ${(parseFloat(investmentAmount) * 2000).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Tokens to Receive
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {calculateTokens().toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Token Price
                      </Typography>
                      <Typography variant="h6" color="info.main">
                        ${(project?.valuation_usd || 0 / (project?.tokenized_percent || 1)).toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  {project?.smart_contract_address && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(23, 209, 198, 0.1)', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Smart Contract Address
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: '#FF7D6A',
                            fontFamily: 'monospace',
                            flex: 1,
                          }}
                        >
                          {project.smart_contract_address}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => copyToClipboard(project.smart_contract_address!)}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                  
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(23, 209, 198, 0.1)', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <SecurityIcon sx={{ mr: 1, color: 'success.main' }} />
                      <Typography variant="body2" color="success.main" fontWeight={600}>
                        Secure Transaction
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      This transaction will be executed on the Hoodi testnet blockchain
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ textAlign: 'center', py: 4 }}>
              {success ? (
                <>
                  <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom color="success.main">
                    Investment Successful!
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Your investment has been processed successfully on Hoodi testnet
                  </Typography>
                  
                  {transactionHash && (
                    <Card sx={{ mt: 2 }}>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Transaction Hash
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontFamily: 'monospace',
                              flex: 1,
                              wordBreak: 'break-all',
                            }}
                          >
                            {transactionHash}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={() => copyToClipboard(transactionHash)}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Link 
                          href={getExplorerUrl(transactionHash)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          View on Explorer
                          <ExternalLinkIcon fontSize="small" />
                        </Link>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <>
                  <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom color="error.main">
                    Investment Failed
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {error || 'Something went wrong. Please try again.'}
                  </Typography>
                </>
              )}
            </Box>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PaymentIcon sx={{ mr: 1 }} />
          Invest in {project?.title}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {renderStepContent(activeStep)}
      </DialogContent>
      
      <DialogActions>
        {activeStep < 3 && (
          <>
            <Button onClick={onClose} disabled={loading || isTransactionPending || isPending}>
              Cancel
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {activeStep > 0 && (
                <Button onClick={handleBack} disabled={loading || isTransactionPending || isPending}>
                  Back
                </Button>
              )}
              {activeStep === steps.length - 2 ? (
                <Button
                  onClick={handleInvest}
                  variant="contained"
                  disabled={loading || isTransactionPending || isPending || !project?.smart_contract_address}
                  startIcon={loading || isTransactionPending || isPending ? <CircularProgress size={20} /> : <PaymentIcon />}
                  sx={{
                    background: 'linear-gradient(45deg, #1E40AF, #3B82F6)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1E3A8A, #2563EB)',
                    },
                  }}
                >
                  {loading || isTransactionPending || isPending ? 'Processing...' : 'Invest Now'}
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  variant="contained"
                  disabled={loading || isTransactionPending || isPending}
                >
                  Next
                </Button>
              )}
            </Box>
          </>
        )}
        
        {activeStep === 3 && (
          <Button
            onClick={onClose}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InvestmentModal; 